import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/* v9 · Pestañas nuevas del centro de control: contactos y planes, costes e historial de «Ver como». */

export const LEVEL_BADGE = {
  listo: { label: "Listo", color: "#7FD4A8" },
  mejorable: { label: "Mejorable", color: "#E0B970" },
  arregla: { label: "Arreglar", color: "#F1707A" },
};

const card = "rounded-xl border border-[var(--ca-line)] p-5";
const cardStyle = { background: "var(--ca-surface)" };
const th = "px-3 py-2.5 font-semibold";
const td = "px-3 py-2.5";

// v26c: coste por lead con 3 decimales (los importes son de céntimos)
const usdLead = (v) => (typeof v === "number" ? `$${v.toLocaleString("es-ES", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}` : "—");
const usd = (v) => (typeof v === "number" ? `$${v.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");
const monthName = (k) => {
  try {
    const [y, m] = k.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("es-ES", { month: "short", year: "numeric" });
  } catch { return k; }
};
const fmtDateTime = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(v); }
};

function Tile({ label, value, hint, tone }) {
  return (
    <div className="rounded-lg border border-[var(--ca-line)] p-3" title={hint || ""}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ca-subtle)]">{label}</p>
      <p className="mt-1 text-xl font-extrabold" style={{ color: tone || "var(--ca-text)" }}>{value ?? "—"}</p>
      {hint && <p className="mt-0.5 text-[11px] leading-snug text-[var(--ca-subtle)]">{hint}</p>}
    </div>
  );
}

function Spinner() {
  return <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={22} style={{ color: "var(--ca-gold)" }} /></div>;
}

/* ------------------------------------------------------------------ Contactos y planes */
export function AdminActivity({ metrics }) {
  if (!metrics) return <Spinner />;
  const funnel = metrics.crm_funnel_ordered || [];
  const max = Math.max(1, ...funnel.map((f) => f.count));
  const p = metrics.plans || {};
  return (
    <div className="space-y-6" data-testid="admin-activity">
      <section className={card} style={cardStyle}>
        <h3 className="text-sm font-bold text-[var(--ca-text)]">Planes de siguiente acción</h3>
        <p className="mt-0.5 text-xs text-[var(--ca-subtle)]">Todos los usuarios, sin contar la demo. {p.contacts ?? 0} contactos en total.</p>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Tile label="Con plan" value={p.with_plan} hint={`${p.ai ?? 0} con IA · ${p.rules ?? 0} con reglas`} />
          <Tile label="Para hoy" value={p.due_today} />
          <Tile label="Atrasados" value={p.overdue} tone={p.overdue ? "#F1707A" : undefined} hint="La fecha del paso ya pasó y nadie lo ha hecho." />
          <Tile label="Esperando a la IA" value={p.pending_ai} hint="Plan provisional de reglas; la IA aún no lo ha rehecho." />
          <Tile label="En pausa" value={p.paused} />
          <Tile label="Bloqueados" value={p.blocked} hint="Marcados como «No contactar»." />
          <Tile label="Activos sin plan" value={p.no_plan_active} tone={p.no_plan_active ? "#E0B970" : undefined} hint="Contactos abiertos que no tienen plan (antiguos o con error)." />
          <Tile label="Llamadas a la IA" value={metrics.ai_calls} />
        </div>
      </section>

      <section className={card} style={cardStyle} data-testid="admin-funnel">
        <h3 className="text-sm font-bold text-[var(--ca-text)]">Embudo del CRM</h3>
        <p className="mt-0.5 text-xs text-[var(--ca-subtle)]">Cuántos contactos hay ahora mismo en cada etapa.</p>
        {funnel.length === 0 ? (
          <p className="mt-4 text-sm text-[var(--ca-subtle)]">Todavía no hay contactos reales.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {funnel.map((f) => (
              <div key={f.status} className="grid grid-cols-[140px_1fr_48px] items-center gap-3 text-xs">
                <span className="truncate text-[var(--ca-muted)]">{f.label}</span>
                <span className="h-2.5 overflow-hidden rounded-full" style={{ background: "var(--ca-line)" }}>
                  <span className="block h-full rounded-full" style={{ width: `${Math.max(3, (f.count / max) * 100)}%`, background: "var(--ca-gold)" }} />
                </span>
                <span className="text-right font-semibold text-[var(--ca-text)]">{f.count}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ Costes */
export function AdminCosts() {
  const [months, setMonths] = useState(6);
  const [withTests, setWithTests] = useState(true);
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/admin/costs", { params: { months, include_tests: withTests } }).then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e)); setData({ error: true }); });
  }, [months, withTests]);

  if (!data) return <Spinner />;
  if (data.error) return <p className="text-sm text-[var(--ca-subtle)]">No se han podido cargar los costes.</p>;
  const t = data.total || {};
  const unknown = (t.ai_unknown || 0) + (t.searches_unknown || 0);
  return (
    <div className="space-y-6" data-testid="admin-costs">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-[var(--ca-subtle)]">En dólares, que es como cobran OpenAI y los proveedores de datos.</p>
        <div className="flex flex-wrap items-center gap-1.5">
          <label className="mr-2 inline-flex items-center gap-1.5 text-xs text-[var(--ca-subtle)]" data-testid="admin-costs-tests">
            <input type="checkbox" checked={withTests} onChange={(e) => setWithTests(e.target.checked)} style={{ accentColor: "var(--ca-gold)" }} />
            Incluir cuentas de prueba
          </label>
          {[3, 6, 12].map((m) => (
            <button key={m} type="button" onClick={() => setMonths(m)} data-testid={`admin-costs-${m}`}
              className={`rounded-md border px-3 py-1 text-xs ${months === m ? "border-[var(--ca-gold)] font-semibold text-[var(--ca-text)]" : "border-[var(--ca-line)] text-[var(--ca-subtle)]"}`}>
              {m} meses
            </button>
          ))}
        </div>
      </div>

      {(data.provider_alerts || []).length > 0 && (
        <div className="rounded-lg border border-[#E0B970] p-3 text-sm" data-testid="admin-provider-alerts">
          {(data.provider_alerts || []).map((a) => (
            <p key={`${a.provider}-${a.status}`}>
              <strong>{a.provider === "tikhub" ? "TikHub" : a.provider === "apify" ? "Apify" : a.provider}</strong>{" "}
              {a.status === "sin_saldo" ? "se ha quedado sin saldo: recárgalo para que las búsquedas no se queden cortas."
                : a.status === "fallo" ? `rechaza las peticiones (${a.detail || "error de cuenta"}): revisa el límite de uso, el saldo o el token en console.apify.com.`
                : "está limitando las peticiones (demasiadas búsquedas a la vez); la app reintenta sola."}
              {" "}<span className="text-xs text-[var(--ca-subtle)]">Último aviso: {String(a.at || "").slice(0, 16).replace("T", " ")} · {a.veces || 1} veces</span>
            </p>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Tile label="Total" value={usd(t.total_usd)} />
        <Tile label="IA" value={usd(t.ai_usd)} hint={`${t.ai_calls ?? 0} llamadas · ${t.ai_errors ?? 0} con error`} />
        <Tile label="Proveedores" value={usd(t.provider_usd)} hint={`${t.searches ?? 0} búsquedas · TikHub y Apify, estimado por llamadas y perfiles`} />
        <Tile label="Sin coste conocido" value={unknown} tone={unknown ? "#E0B970" : undefined}
          hint="Llamadas o búsquedas sin precio registrado: el total real puede ser algo mayor." />
        <Tile label="Leads entregados" value={t.leads ?? 0} hint="Mostrados + en reserva de cada búsqueda." />
        <Tile label="Coste por lead" value={usdLead(t.cost_per_lead)} tone={t.cost_alert ? "#E07070" : undefined}
          hint={`IA + proveedores ÷ leads. Aviso a partir de ${usdLead(data.cost_per_lead_alert)}.`} />
      </div>

      {(data.alerts || []).length > 0 && (
        <p className="rounded-md border px-3 py-2 text-xs" style={{ borderColor: "#E07070", color: "#E07070" }} data-testid="admin-costs-alerts">
          Coste por lead por encima de {usdLead(data.cost_per_lead_alert)}: {data.alerts.join(", ")}. Si sigue así, conviene revisar el precio del plan.
        </p>
      )}

      {(data.routes || []).length > 0 && (
        <section className={card} style={cardStyle}>
          <h3 className="text-sm font-bold text-[var(--ca-text)]">Por tipo de búsqueda</h3>
          <p className="mt-0.5 text-xs text-[var(--ca-subtle)]">Solo proveedores de datos (la IA no se puede repartir por búsqueda).</p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="admin-costs-routes">
              <thead><tr className="border-b border-[var(--ca-line)] text-[11px] uppercase tracking-wide text-[var(--ca-subtle)]">
                <th className={th}>Tipo</th><th className={`${th} text-right`}>Búsquedas</th><th className={`${th} text-right`}>Leads</th>
                <th className={`${th} text-right`}>Leads/búsqueda</th><th className={`${th} text-right`}>TikHub</th><th className={`${th} text-right`}>Apify</th>
                <th className={`${th} text-right`}>Proveedores por lead</th>
              </tr></thead>
              <tbody>
                {data.routes.map((r) => (
                  <tr key={r.kind} className="border-b border-[var(--ca-line)] last:border-0 text-[var(--ca-text)]">
                    <td className={td}>{r.label}</td>
                    <td className={`${td} text-right text-[var(--ca-muted)]`}>{r.searches}</td>
                    <td className={`${td} text-right`}>{r.leads}</td>
                    <td className={`${td} text-right text-[var(--ca-muted)]`}>{r.leads_per_search ?? "—"}</td>
                    <td className={`${td} text-right`}>{usd(r.tikhub_usd)}</td>
                    <td className={`${td} text-right`}>{usd(r.apify_usd)}</td>
                    <td className={`${td} text-right font-semibold`}>{usdLead(r.provider_per_lead)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className={card} style={cardStyle}>
        <h3 className="text-sm font-bold text-[var(--ca-text)]">Por mes</h3>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="admin-costs-months">
            <thead><tr className="border-b border-[var(--ca-line)] text-[11px] uppercase tracking-wide text-[var(--ca-subtle)]">
              <th className={th}>Mes</th><th className={`${th} text-right`}>IA</th><th className={`${th} text-right`}>Proveedores</th>
              <th className={`${th} text-right`}>Total</th><th className={`${th} text-right`}>Llamadas IA</th><th className={`${th} text-right`}>Búsquedas</th>
              <th className={`${th} text-right`}>Leads</th><th className={`${th} text-right`}>Coste/lead</th>
            </tr></thead>
            <tbody>
              {[...(data.months || [])].reverse().map((m) => (
                <tr key={m.month} className="border-b border-[var(--ca-line)] last:border-0 text-[var(--ca-text)]">
                  <td className={`${td} capitalize`}>{monthName(m.month)}</td>
                  <td className={`${td} text-right`}>{usd(m.ai_usd)}</td>
                  <td className={`${td} text-right`}>{usd(m.provider_usd)}</td>
                  <td className={`${td} text-right font-semibold`}>{usd(m.total_usd)}</td>
                  <td className={`${td} text-right text-[var(--ca-muted)]`}>{m.ai_calls}</td>
                  <td className={`${td} text-right text-[var(--ca-muted)]`}>{m.searches}</td>
                  <td className={`${td} text-right`}>{m.leads ?? 0}</td>
                  <td className={`${td} text-right font-semibold`} style={m.cost_alert ? { color: "#E07070" } : undefined}>{usdLead(m.cost_per_lead)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={card} style={cardStyle}>
        <h3 className="text-sm font-bold text-[var(--ca-text)]">Por usuario</h3>
        <p className="mt-0.5 text-xs text-[var(--ca-subtle)]">Los 50 que más gastan en el periodo.</p>
        {(data.users || []).length === 0 ? (
          <p className="mt-3 text-sm text-[var(--ca-subtle)]">Sin gasto en este periodo.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="admin-costs-users">
              <thead><tr className="border-b border-[var(--ca-line)] text-[11px] uppercase tracking-wide text-[var(--ca-subtle)]">
                <th className={th}>Usuario</th><th className={`${th} text-right`}>IA</th><th className={`${th} text-right`}>Proveedores</th>
                <th className={`${th} text-right`}>Total</th><th className={`${th} text-right`}>Llamadas IA</th><th className={`${th} text-right`}>Búsquedas</th>
              <th className={`${th} text-right`}>Leads</th><th className={`${th} text-right`}>Coste/lead</th>
              </tr></thead>
              <tbody>
                {data.users.map((u) => (
                  <tr key={u.user_id || "none"} className="border-b border-[var(--ca-line)] last:border-0 text-[var(--ca-text)]">
                    <td className={`${td} text-[var(--ca-muted)]`}>{u.email}</td>
                    <td className={`${td} text-right`}>{usd(u.ai_usd)}</td>
                    <td className={`${td} text-right`}>{usd(u.provider_usd)}</td>
                    <td className={`${td} text-right font-semibold`}>{usd(u.total_usd)}</td>
                    <td className={`${td} text-right text-[var(--ca-muted)]`}>{u.ai_calls}</td>
                    <td className={`${td} text-right text-[var(--ca-muted)]`}>{u.searches}</td>
                  <td className={`${td} text-right`}>{u.leads ?? 0}</td>
                  <td className={`${td} text-right font-semibold`} style={u.cost_alert ? { color: "#E07070" } : undefined}>{usdLead(u.cost_per_lead)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ Historial de admin */
const ACTION_LABEL = { impersonate_readonly: "Ver como", plan_changed: "Cambio de plan", user_deleted: "Cuenta eliminada", pricing_changed: "Precios / oferta",
  team_member_added: "Equipo: persona añadida", team_member_removed: "Equipo: persona quitada" };
export function AdminAudit() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/audit").then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e)); setData({ items: [] }); });
  }, []);
  if (!data) return <Spinner />;
  return (
    <section className={card} style={cardStyle} data-testid="admin-audit">
      <h3 className="text-sm font-bold text-[var(--ca-text)]">Historial de admin</h3>
      <p className="mt-0.5 text-xs text-[var(--ca-subtle)]">Cada «Ver como» (siempre en solo lectura), cambio de plan y cuenta eliminada. {data.total ?? 0} en total.</p>
      {(data.items || []).length === 0 ? (
        <p className="mt-3 text-sm text-[var(--ca-subtle)]">Todavía no hay nada registrado.</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-b border-[var(--ca-line)] text-[11px] uppercase tracking-wide text-[var(--ca-subtle)]">
              <th className={th}>Cuándo</th><th className={th}>Administrador</th><th className={th}>Acción</th><th className={th}>Cuenta</th>
            </tr></thead>
            <tbody>
              {data.items.map((a, i) => (
                <tr key={i} className="border-b border-[var(--ca-line)] last:border-0 text-[var(--ca-text)]">
                  <td className={`${td} whitespace-nowrap text-[var(--ca-muted)]`}>{fmtDateTime(a.ts)}</td>
                  <td className={td}>{a.admin_email || "—"}</td>
                  <td className={td}>
                    <span style={{ color: a.action === "user_deleted" ? "#F1707A" : undefined }}>{ACTION_LABEL[a.action] || a.action}</span>
                    {a.detail && <span className="block text-[11px] text-[var(--ca-subtle)]">{a.detail}</span>}
                  </td>
                  <td className={td}>{a.target_email || a.target_user_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ Precios y ofertas (v11) */
const inputCls = "w-full rounded-lg border border-[var(--ca-line)] px-3 py-2 text-sm";
const inputStyle = { background: "var(--ca-surface)", color: "var(--ca-text)" };

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-[var(--ca-subtle)]">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-[var(--ca-subtle)]">{hint}</span>}
    </label>
  );
}

export function AdminPricing() {
  const [form, setForm] = useState(null);
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/pricing").then((r) => { setForm(r.data.settings); setMeta(r.data); })
      .catch((e) => { toast.error(apiError(e)); setForm({ error: true }); });
  }, []);

  if (!form) return <Spinner />;
  if (form.error) return <p className="text-sm text-[var(--ca-subtle)]">No se han podido cargar los precios.</p>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setOffer = (k, v) => setForm((f) => ({ ...f, offer: { ...f.offer, [k]: v } }));
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(String(v).replace(",", ".")));
  const o = form.offer || {};
  const today = new Date().toISOString().slice(0, 10);
  const live = o.active && o.monthly && o.ends_at && o.ends_at >= today;

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        pro_monthly: num(form.pro_monthly), pro_yearly: num(form.pro_yearly),
        business_monthly: num(form.business_monthly), business_yearly: num(form.business_yearly),
        extra_user_monthly: num(form.extra_user_monthly), extra_user_yearly: num(form.extra_user_yearly),
        offer: { active: !!o.active, label: o.label || "", monthly: num(o.monthly), yearly: num(o.yearly), ends_at: o.ends_at || "" },
      };
      const r = await api.put("/admin/pricing", body);
      setForm(r.data.settings);
      setMeta((m) => ({ ...(m || {}), public: r.data.public }));
      toast.success(r.data.public.offer ? "Guardado. La oferta ya se ve en la landing y en la app." : "Precios guardados.");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-pricing">
      <section className={card} style={cardStyle}>
        <h3 className="text-sm font-bold text-[var(--ca-text)]">Precios normales</h3>
        <p className="mt-0.5 text-xs text-[var(--ca-subtle)]">En euros. Se ven en la landing y en «Cuenta y plan». El plan Gratis no tiene precio.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Pro · mensual (€)"><input className={inputCls} style={inputStyle} inputMode="decimal" value={form.pro_monthly ?? ""} onChange={(e) => set("pro_monthly", e.target.value)} data-testid="pricing-pro-monthly" /></Field>
          <Field label="Pro · anual (€)"><input className={inputCls} style={inputStyle} inputMode="decimal" value={form.pro_yearly ?? ""} onChange={(e) => set("pro_yearly", e.target.value)} data-testid="pricing-pro-yearly" /></Field>
          <Field label="Business · mensual (€)"><input className={inputCls} style={inputStyle} inputMode="decimal" value={form.business_monthly ?? ""} onChange={(e) => set("business_monthly", e.target.value)} /></Field>
          <Field label="Business · anual (€)"><input className={inputCls} style={inputStyle} inputMode="decimal" value={form.business_yearly ?? ""} onChange={(e) => set("business_yearly", e.target.value)} /></Field>
        </div>
        <div className="mt-5 border-t border-[var(--ca-line)] pt-4">
          <h4 className="text-xs font-bold text-[var(--ca-text)]">Usuario extra en Business</h4>
          <p className="mt-0.5 text-xs text-[var(--ca-subtle)]">Cada persona más allá de las 5 incluidas en Business. Si lo dejas vacío, no se muestra.</p>
          <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Usuario extra · mensual (€)"><input className={inputCls} style={inputStyle} inputMode="decimal" value={form.extra_user_monthly ?? ""} onChange={(e) => set("extra_user_monthly", e.target.value)} data-testid="pricing-extra-monthly" /></Field>
            <Field label="Usuario extra · anual (€)"><input className={inputCls} style={inputStyle} inputMode="decimal" value={form.extra_user_yearly ?? ""} onChange={(e) => set("extra_user_yearly", e.target.value)} data-testid="pricing-extra-yearly" /></Field>
          </div>
        </div>
      </section>

      <section className={card} style={cardStyle}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-[var(--ca-text)]">Oferta del plan Pro</h3>
            <p className="mt-0.5 text-xs text-[var(--ca-subtle)]">Se muestra con el precio normal tachado y se apaga sola al pasar la fecha de fin.</p>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-[var(--ca-text)]">
            <input type="checkbox" checked={!!o.active} onChange={(e) => setOffer("active", e.target.checked)} data-testid="pricing-offer-active" />
            Oferta activada
          </label>
        </div>
        <div className={`mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 ${o.active ? "" : "opacity-50"}`}>
          <Field label="Nombre de la oferta"><input className={inputCls} style={inputStyle} maxLength={60} value={o.label ?? ""} onChange={(e) => setOffer("label", e.target.value)} data-testid="pricing-offer-label" /></Field>
          <Field label="Precio de oferta · mensual (€)"><input className={inputCls} style={inputStyle} inputMode="decimal" value={o.monthly ?? ""} onChange={(e) => setOffer("monthly", e.target.value)} data-testid="pricing-offer-monthly" /></Field>
          <Field label="Precio de oferta · anual (€)" hint="Opcional. Vacío = el anual no tiene oferta."><input className={inputCls} style={inputStyle} inputMode="decimal" value={o.yearly ?? ""} onChange={(e) => setOffer("yearly", e.target.value)} data-testid="pricing-offer-yearly" /></Field>
          <Field label="Termina el (incluido)"><input type="date" className={inputCls} style={inputStyle} value={o.ends_at ?? ""} min={today} onChange={(e) => setOffer("ends_at", e.target.value)} data-testid="pricing-offer-ends" /></Field>
        </div>
        <div className="mt-4 rounded-lg border border-dashed border-[var(--ca-line)] p-3 text-sm" data-testid="pricing-preview">
          <span className="text-[var(--ca-subtle)]">Así se verá ahora: </span>
          {live ? (
            <span className="text-[var(--ca-text)]">
              <s className="text-[var(--ca-subtle)]">{form.pro_monthly} €</s> <b>{o.monthly} €/mes</b> · <span style={{ color: "var(--ca-gold)" }}>{o.label || "Oferta"}</span> hasta el {o.ends_at.split("-").reverse().join("/")}
            </span>
          ) : <span className="text-[var(--ca-text)]">Pro a {form.pro_monthly} €/mes, sin oferta.</span>}
        </div>
        <p className="mt-3 text-[11px] text-[var(--ca-subtle)]">
          Los pagos todavía se gestionan a mano: esto cambia lo que se muestra, no cobra nada. Si quien entra con la oferta conserva ese precio después se decidirá al activar los pagos.
        </p>
      </section>

      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-[var(--ca-subtle)]">{meta?.updated_at ? `Último cambio: ${fmtDateTime(meta.updated_at)} · ${meta.updated_by || ""}` : "Todavía no se ha cambiado nada: se usan los precios de siempre."}</p>
        <button onClick={save} disabled={saving} className="ca-btn-gold ca-btn-sm disabled:opacity-60" data-testid="pricing-save">
          {saving && <Loader2 size={14} className="animate-spin" />} Guardar precios
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Búsquedas vacías (v12) */
const STAGE_COLOR = { zona: "#7FB3E0", sin_resultados: "#F1707A", filtros: "#E0B970", encaje: "#9AA4B2" };

export function AdminEmptySearches() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/admin/empty-searches").then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e)); setData({ items: [] }); });
  }, []);
  if (!data) return <Spinner />;
  const titles = data.stage_titles || {};
  return (
    <div className="space-y-6" data-testid="admin-empty-searches">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Object.keys(titles).map((k) => (
          <Tile key={k} label={titles[k]} value={(data.by_stage || {})[k] || 0} tone={STAGE_COLOR[k]} />
        ))}
      </div>
      <section className={card} style={cardStyle}>
        <h3 className="text-sm font-bold text-[var(--ca-text)]">Últimas búsquedas sin prospectos</h3>
        <p className="mt-0.5 text-xs text-[var(--ca-subtle)]">Por qué salieron vacías y qué se le recomendó al cliente. Ninguna se descontó de su plan.</p>
        {(data.items || []).length === 0 ? (
          <p className="mt-3 text-sm text-[var(--ca-subtle)]">No hay búsquedas vacías.</p>
        ) : (
          <div className="mt-3 divide-y divide-[var(--ca-line)]">
            {data.items.map((it) => {
              const isOpen = open === it.job_id;
              const c = it.criteria || {};
              return (
                <div key={it.job_id} className="py-2.5">
                  <button type="button" onClick={() => setOpen(isOpen ? null : it.job_id)} className="flex w-full items-start justify-between gap-3 text-left">
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-[var(--ca-text)]">{it.email} · {it.avatar || "—"}</span>
                      <span className="block truncate text-xs text-[var(--ca-subtle)]">{(c.keywords || []).join(", ") || "—"}{c.location ? ` · ${c.location}` : ""}</span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-xs font-semibold" style={{ color: STAGE_COLOR[it.stage] }}>{it.title}</span>
                      <span className="block text-[11px] text-[var(--ca-subtle)]">{fmtDateTime(it.when)}</span>
                    </span>
                  </button>
                  {isOpen && (
                    <div className="mt-2 space-y-1.5 rounded-lg border border-[var(--ca-line)] p-3 text-xs text-[var(--ca-muted)]">
                      <p><b className="text-[var(--ca-text)]">Motivo:</b> {it.reason}</p>
                      <p>{it.numbers?.encontradas ?? 0} encontradas · {it.numbers?.descartadas_por_filtros ?? 0} descartadas por filtros · {it.numbers?.analizadas ?? 0} analizadas</p>
                      {it.top_reasons?.length > 0 && <p>Descartes: {it.top_reasons.map((r) => `${r.reason} (${r.count})`).join(" · ")}</p>}
                      <p><b className="text-[var(--ca-text)]">Consejo:</b> {it.advice}</p>
                      {it.suggestion ? (
                        <p><b className="text-[var(--ca-text)]">Búsqueda propuesta ({it.source === "ia" ? "IA" : "reglas"}):</b> {(it.suggestion.keywords || []).join(", ")}{it.suggestion.location ? ` · ${it.suggestion.location}` : " · sin ubicación"}{it.suggestion.reference_accounts?.length ? ` · ${it.suggestion.reference_accounts.join(", ")}` : ""}</p>
                      ) : <p>Sin búsqueda alternativa (no había nada evidente que relajar).</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
